import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lightning, CheckCircle, XCircle, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { centsToUsd, describeAutopayRetry, AUTOPAY_MAX_ATTEMPTS } from "@/lib/billing";
import { retryInvoiceAutopay } from "@/lib/adminAutopay";

/**
 * Admin-only read-only panel that surfaces the last ~20 autopay-related
 * payment_transactions so directors have a live pulse on the cron job
 * without opening the DB.
 *
 * Rows shown: payment_status IN ('autopay','autopay_setup') — a superset
 * of successful cron charges + any setup attempts that flipped via
 * webhook/sync-dev. Status column uses transactions.status
 * (succeeded / failed / pending) which is always populated.
 */
const LIMIT = 20;

const STATUS_META = {
  succeeded: { label: "Charged", tone: "border-primary/30 bg-primary/10 text-primary-hover",   icon: CheckCircle },
  failed:    { label: "Failed",  tone: "border-[#C6851E]/40 bg-[#C6851E]/10 text-[#C6851E]",    icon: XCircle },
  pending:   { label: "Pending", tone: "border-neutral-200 bg-neutral-100 text-neutral-600",    icon: ArrowClockwise },
  canceled:  { label: "Cancelled", tone: "border-neutral-200 bg-neutral-100 text-neutral-500",  icon: XCircle },
};

export default function AutopayActivityPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  const load = async () => {
    if (!isSupabaseConfigured) return;
    try {
      const { data, error } = await supabase
        .from("payment_transactions")
        .select(`
          id, created_at, status, payment_status, amount_cents, currency,
          session_id, metadata,
          invoice:invoices(id, status, autopay_attempt_count, autopay_next_retry_at),
          billing_profile:billing_profiles(
            id,
            autopay_status,
            stripe_customer_id,
            stripe_payment_method_id,
            family:families(id, display_name)
          )
        `)
        .in("payment_status", ["autopay", "autopay_setup"])
        .order("created_at", { ascending: false })
        .limit(LIMIT);
      if (error) throw error;
      setRows(data || []);
    } catch (err) {
      toast.error(err.message || "Could not load autopay activity");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onRefresh = () => { setRefreshing(true); load(); };

  const onRetry = async (invoiceId) => {
    setRetryingId(invoiceId);
    try {
      const res = await retryInvoiceAutopay(invoiceId);
      if (res.status === "charged") {
        toast.success(`Charged ${centsToUsd(res.amount_cents)} · ${res.family_name || ""}`);
      } else {
        toast.error(`Retry ${res.status}${res.reason ? ` · ${res.reason}` : ""}`);
      }
      await load();
    } catch (err) {
      toast.error(err.message || "Retry failed");
    } finally {
      setRetryingId(null);
    }
  };

  const charged = rows.filter((r) => r.status === "succeeded" && r.payment_status === "autopay").length;
  const failed = rows.filter((r) => r.status === "failed").length;

  return (
    <section
      data-testid="admin-autopay-activity"
      className="rounded-2xl border border-neutral-200 bg-white"
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 px-5 py-4">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-secondary/30">
            <Lightning size={16} weight="duotone" className="text-primary-hover" />
          </span>
          <div>
            <p className="font-heading text-base font-semibold tracking-tight text-neutral-900">
              Autopay activity
            </p>
            <p className="text-xs text-neutral-500">
              {rows.length === 0
                ? "Cron-driven charges on due invoices"
                : `Last ${rows.length} attempt${rows.length === 1 ? "" : "s"} · ${charged} charged · ${failed} failed`}
            </p>
          </div>
        </div>
        <button
          type="button"
          data-testid="admin-autopay-activity-refresh"
          onClick={onRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-50 disabled:opacity-60"
        >
          <ArrowClockwise size={11} weight="bold" className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="h-6 w-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="admin-autopay-activity-empty" className="p-8 text-center">
          <p className="text-sm text-neutral-500">
            No autopay activity yet. When families enroll in autopay and the
            cron runs, attempts will show up here.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-neutral-100" data-testid="admin-autopay-activity-list">
          {rows.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.pending;
            const Icon = meta.icon;
            const isSetup = r.payment_status === "autopay_setup";
            const familyName = r.billing_profile?.family?.display_name || "—";
            const familyId = r.billing_profile?.family?.id;
            const invoiceId = r.invoice?.id;
            const reason = r.metadata?.error || r.metadata?.reason || null;
            const retry = describeAutopayRetry(r.invoice);
            const maxed = (r.invoice?.autopay_attempt_count ?? 0) >= AUTOPAY_MAX_ATTEMPTS;
            const canRetry =
              r.status === "failed" &&
              !isSetup &&
              r.invoice?.status === "issued" &&
              !maxed &&
              r.billing_profile?.stripe_customer_id &&
              r.billing_profile?.stripe_payment_method_id;
            return (
              <li
                key={r.id}
                data-testid={`admin-autopay-activity-row-${r.id}`}
                data-retry-kind={retry?.kind || "none"}
                className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Icon
                    size={18}
                    weight="duotone"
                    className={
                      r.status === "succeeded" ? "text-primary-hover" :
                      r.status === "failed"    ? "text-[#C6851E]"   :
                                                 "text-neutral-400"
                    }
                  />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      {familyId ? (
                        <Link
                          to={`/admin/billing/${familyId}`}
                          className="truncate font-semibold text-neutral-900 hover:text-primary-hover"
                        >
                          {familyName}
                        </Link>
                      ) : (
                        <span className="truncate font-semibold text-neutral-900">{familyName}</span>
                      )}
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.tone}`}
                      >
                        {isSetup ? (r.status === "succeeded" ? "Setup OK" : "Setup failed") : meta.label}
                      </span>
                      {retry && (
                        <span
                          data-testid={`admin-autopay-activity-retry-hint-${invoiceId}`}
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            retry.kind === "needs_action"
                              ? "bg-[#C6851E]/10 text-[#8A5A12]"
                              : "bg-amber-50 text-amber-900"
                          }`}
                        >
                          {retry.label}
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-[11px] text-neutral-500">
                      {formatRelative(r.created_at)}
                      {invoiceId ? ` · Invoice ${invoiceId.slice(0, 8)}` : ""}
                      {r.invoice?.autopay_attempt_count != null
                        ? ` · attempt ${r.invoice.autopay_attempt_count}/${AUTOPAY_MAX_ATTEMPTS}`
                        : ""}
                      {reason ? ` · ${reason}` : ""}
                    </p>
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-3">
                  <span className="font-mono text-sm text-neutral-700">
                    {centsToUsd(r.amount_cents)}
                  </span>
                  {canRetry && (
                    <button
                      type="button"
                      data-testid={`admin-autopay-activity-retry-${invoiceId}`}
                      onClick={() => onRetry(invoiceId)}
                      disabled={retryingId === invoiceId}
                      title="Manual retry bypasses the scheduled retry time but still respects the max-attempts cap."
                      className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-white px-2.5 py-1 text-[11px] font-semibold text-primary-hover hover:bg-primary/5 disabled:opacity-60"
                    >
                      <ArrowClockwise
                        size={11}
                        weight="bold"
                        className={retryingId === invoiceId ? "animate-spin" : ""}
                      />
                      {retryingId === invoiceId ? "Retrying…" : "Retry now"}
                    </button>
                  )}
                  {maxed && r.status === "failed" && !isSetup && r.invoice?.status === "issued" && (
                    <span
                      data-testid={`admin-autopay-activity-maxed-${invoiceId}`}
                      className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-[11px] font-semibold text-neutral-500"
                    >
                      Max attempts
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function formatRelative(iso) {
  if (!iso) return "—";
  try {
    const dt = new Date(iso);
    const diffMs = Date.now() - dt.getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return dt.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}
